import { useId, CSSProperties } from 'react'

interface VolumetricFlaskProps {
  size?: 50 | 100 | 250 | 500
  fillLevel?: number // 0-100
  fillColor?: string
  active?: boolean
  onClick?: () => void
  disabled?: boolean
  label?: string
  className?: string
}

const VolumetricFlask = ({
  size = 100,
  fillLevel = 0,
  fillColor = '#7DD3FC',
  active = false,
  onClick,
  disabled = false,
  label = '',
  className = ''
}: VolumetricFlaskProps) => {
  const id = useId()

  // Scale based on flask size
  const scale = size === 50 ? 0.7 : size === 100 ? 0.85 : size === 250 ? 1 : 1.15
  const width = Math.round(80 * scale)
  const height = Math.round(130 * scale)

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  // Flask dimensions (pear-shaped body with narrow neck)
  const neckTop = 10
  const neckBottom = 40
  const neckWidth = 8
  const bodyTop = 45
  const bodyBottom = 110
  const bodyMaxWidth = 60
  const centerX = 40

  // Calibration line position (at the neck-body junction)
  const calibrationY = 42

  // Calculate fill
  const totalVolume = bodyBottom - neckTop
  const fillAmount = (fillLevel / 100) * totalVolume
  const fillY = bodyBottom - fillAmount

  // Determine if fill is in body or neck
  const fillInBody = fillY > bodyTop
  const fillInNeck = fillY < bodyTop

  return (
    <svg
      viewBox="0 0 80 130"
      width={width}
      height={height}
      onClick={disabled ? undefined : onClick}
      style={containerStyle}
      className={className}
    >
      <defs>
        {/* Glass gradient */}
        <linearGradient id={`vf-glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(170,200,220,0.7)" />
          <stop offset="12%" stopColor="rgba(220,240,255,0.4)" />
          <stop offset="25%" stopColor="rgba(255,255,255,0.25)" />
          <stop offset="50%" stopColor="rgba(210,230,245,0.15)" />
          <stop offset="75%" stopColor="rgba(255,255,255,0.2)" />
          <stop offset="88%" stopColor="rgba(220,240,255,0.35)" />
          <stop offset="100%" stopColor="rgba(170,200,220,0.6)" />
        </linearGradient>

        {/* Liquid gradient */}
        <linearGradient id={`vf-liquid-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={fillColor} stopOpacity="0.8" />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0.95" />
        </linearGradient>

        {/* Shadow */}
        <filter id={`vf-shadow-${id}`} x="-15%" y="-5%" width="130%" height="115%">
          <feDropShadow dx="2" dy="3" stdDeviation="2" floodOpacity="0.25" />
        </filter>

        {/* Active glow */}
        <filter id={`vf-glow-${id}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Clip path for liquid in body */}
        <clipPath id={`vf-body-clip-${id}`}>
          <path
            d={`M${centerX - neckWidth/2} ${bodyTop}
                C${centerX - neckWidth/2 - 5} ${bodyTop + 10}
                  ${centerX - bodyMaxWidth/2} ${bodyTop + 20}
                  ${centerX - bodyMaxWidth/2} ${bodyBottom - 15}
                Q${centerX - bodyMaxWidth/2} ${bodyBottom}
                  ${centerX} ${bodyBottom}
                Q${centerX + bodyMaxWidth/2} ${bodyBottom}
                  ${centerX + bodyMaxWidth/2} ${bodyBottom - 15}
                C${centerX + bodyMaxWidth/2} ${bodyTop + 20}
                  ${centerX + neckWidth/2 + 5} ${bodyTop + 10}
                  ${centerX + neckWidth/2} ${bodyTop}
                Z`}
          />
        </clipPath>

        {/* Clip path for liquid in neck */}
        <clipPath id={`vf-neck-clip-${id}`}>
          <rect
            x={centerX - neckWidth/2}
            y={neckTop}
            width={neckWidth}
            height={bodyTop - neckTop + 5}
          />
        </clipPath>
      </defs>

      {/* Active glow */}
      {active && (
        <ellipse
          cx={centerX}
          cy={75}
          rx={bodyMaxWidth/2 + 8}
          ry={50}
          fill="none"
          stroke="var(--primary, #FF5A36)"
          strokeWidth="2.5"
          filter={`url(#vf-glow-${id})`}
        >
          <animate
            attributeName="stroke-opacity"
            values="1;0.4;1"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </ellipse>
      )}

      <g filter={`url(#vf-shadow-${id})`}>
        {/* Neck */}
        <rect
          x={centerX - neckWidth/2}
          y={neckTop}
          width={neckWidth}
          height={bodyTop - neckTop + 2}
          fill={`url(#vf-glass-${id})`}
          stroke="#7A9BB8"
          strokeWidth="1.5"
        />

        {/* Flask body (pear shape) */}
        <path
          d={`M${centerX - neckWidth/2} ${bodyTop}
              C${centerX - neckWidth/2 - 5} ${bodyTop + 10}
                ${centerX - bodyMaxWidth/2} ${bodyTop + 20}
                ${centerX - bodyMaxWidth/2} ${bodyBottom - 15}
              Q${centerX - bodyMaxWidth/2} ${bodyBottom}
                ${centerX} ${bodyBottom}
              Q${centerX + bodyMaxWidth/2} ${bodyBottom}
                ${centerX + bodyMaxWidth/2} ${bodyBottom - 15}
              C${centerX + bodyMaxWidth/2} ${bodyTop + 20}
                ${centerX + neckWidth/2 + 5} ${bodyTop + 10}
                ${centerX + neckWidth/2} ${bodyTop}
              Z`}
          fill={`url(#vf-glass-${id})`}
          stroke="#7A9BB8"
          strokeWidth="1.5"
        />

        {/* Rim at top of neck */}
        <ellipse
          cx={centerX}
          cy={neckTop}
          rx={neckWidth/2 + 2}
          ry="2"
          fill="#8AAFC8"
          stroke="#6A8AA5"
          strokeWidth="0.5"
        />

        {/* Calibration line (graduation mark) */}
        <line
          x1={centerX - neckWidth/2 - 4}
          y1={calibrationY}
          x2={centerX + neckWidth/2 + 4}
          y2={calibrationY}
          stroke="#5A7A95"
          strokeWidth="1.5"
        />
        <text
          x={centerX + neckWidth/2 + 8}
          y={calibrationY + 3}
          fontSize="7"
          fill="#5A7A95"
          fontFamily="Arial, sans-serif"
        >
          {size} mL
        </text>
      </g>

      {/* Liquid in body */}
      {fillLevel > 0 && fillInBody && (
        <g clipPath={`url(#vf-body-clip-${id})`}>
          <rect
            x={centerX - bodyMaxWidth/2}
            y={fillY}
            width={bodyMaxWidth}
            height={bodyBottom - fillY}
            fill={`url(#vf-liquid-${id})`}
          />
          {/* Liquid surface highlight */}
          <ellipse
            cx={centerX}
            cy={fillY}
            rx={(bodyMaxWidth/2) * Math.min(1, (bodyBottom - fillY) / 30)}
            ry="2"
            fill={fillColor}
            opacity="0.85"
          />
        </g>
      )}

      {/* Liquid in neck (when filled above body) */}
      {fillLevel > 0 && fillInNeck && (
        <>
          {/* Full body */}
          <g clipPath={`url(#vf-body-clip-${id})`}>
            <rect
              x={centerX - bodyMaxWidth/2}
              y={bodyTop}
              width={bodyMaxWidth}
              height={bodyBottom - bodyTop}
              fill={`url(#vf-liquid-${id})`}
            />
          </g>
          {/* Neck fill */}
          <g clipPath={`url(#vf-neck-clip-${id})`}>
            <rect
              x={centerX - neckWidth/2 + 1}
              y={fillY}
              width={neckWidth - 2}
              height={bodyTop - fillY + 5}
              fill={`url(#vf-liquid-${id})`}
            />
            {/* Liquid surface in neck */}
            <ellipse
              cx={centerX}
              cy={fillY}
              rx={neckWidth/2 - 2}
              ry="1"
              fill={fillColor}
              opacity="0.9"
            />
          </g>
        </>
      )}

      {/* Glass highlights */}
      <path
        d={`M${centerX - bodyMaxWidth/2 + 5} ${bodyTop + 25}
            Q${centerX - bodyMaxWidth/2 + 3} ${bodyBottom - 30}
              ${centerX - bodyMaxWidth/2 + 8} ${bodyBottom - 10}`}
        stroke="rgba(255,255,255,0.5)"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      <rect
        x={centerX - neckWidth/2 + 1}
        y={neckTop + 5}
        width="2"
        height={bodyTop - neckTop - 10}
        fill="rgba(255,255,255,0.4)"
        rx="1"
      />

      {/* Label */}
      {label && (
        <text
          x={centerX}
          y="125"
          fontSize="9"
          fill="var(--text-primary, #FFFFFF)"
          textAnchor="middle"
          fontWeight="500"
          fontFamily="Arial, sans-serif"
        >
          {label}
        </text>
      )}
    </svg>
  )
}

export default VolumetricFlask
